// includes/js-src/janus-engine/pl/closure.ts — the definition CLOSURE a root needs before it can be priced (Janus C2 §6.3,
// §14 amendment 4). Mirrors tools/probes/c2_pl_oracle.php `$closure`: PRICED definitions (every ability bucket entry, the four
// imbue layers, prepared spells — depth 1; their content is priced) and XREF TARGETS (the status / charge-grant / summon /
// kill-transform / shapeshift / unlock families on the root and on every priced def — read for their STORED bare PL only).
// The entry resolves every id BEFORE the first pass runs; a missing / private one is reported, never floored (D7).
import { readInventoryTemplateId, type ItemFieldRow } from '../compile/item-field-reader';
import { plCatalogs, plCostContract, checkUuid, type PlCostContract } from './contract';
import { plCostLayerEntryUuid, abilityEntryId } from './layer-cells';
import type { PlReadContext, Row } from './read-context';
import { isObj } from './read-context';
import { entriesList, heldInventoryEntries } from './economy';
import { entryRestoreItemArr } from './passes';

export const PL_SPELL_LAYERS = ['at_will_spell', 'innate_spell_per_long_rest', 'charged_spell', 'charged_ability'] as const;
export const PL_STATUS_LAYERS = ['status_effect_applied_to_target_after_cast', 'status_effect_applied_to_target_on_hit', 'status_effect_applied_to_target_after_contesting_roll_failed',
    'status_effect_applied_to_caster_on_hit', 'status_effect_applied_to_caster_after_cast', 'status_effect_applied_to_caster_after_contesting_roll_failed', 'status_effect_applied_to_caster_when_contestee_wins',
    'status_effect_removed_from_target_after_cast', 'status_effect_removed_from_target_on_hit', 'status_effect_removed_from_target_after_contesting_roll_failed',
    'status_effect_removed_from_caster_after_cast', 'status_effect_removed_from_caster_on_hit', 'status_effect_removed_from_caster_after_contesting_roll_failed'] as const;
export const PL_CHARGE_GRANT_LAYERS = ['add_charges_to_ability', 'add_charges_to_ability_for_target', 'increase_max_charges_of_ability'] as const;
export const PL_SHAPESHIFT_LAYERS = ['shapeshift', 'user_choice_1_shapeshift', 'user_choice_2_shapeshift', 'user_choice_3_shapeshift', 'user_choice_4_shapeshift'] as const;
export const PL_UNLOCK_BASES = ['spell', 'at_will_spell', 'innate_spell_per_long_rest', 'ability', 'passive_ability', 'quick_ability', 'full_ability', 'free_ability', 'reaction_ability', 'attack_ability', 'opportunity_attack_ability', 'menta', 'curse', 'sanguine', 'feat', 'summon'] as const;
// Public content currently tops out just above 256. 512 remains bounded under the claim endpoint's
// 64 KiB field limit with today's 64-hex revision tokens, while 513 is refused on both sides.
export const PL_CLOSURE_MAX_IDS = 512;

export interface PlClosure {
    priced: string[];
    /** Cross-reference targets that are NOT also priced definitions (the fixture's `targets` column). */
    targets: string[];
    /** EVERY cross-reference target, including the ones that are priced definitions too (a battery passive granting charges
     * to an ability the same carrier also holds) — these need their STORED PL as well as their layers. */
    xref: string[];
    total: number;
}

/** The id calc_pl derives for a bucket entry (ability_entry_id ?: pl_cost_layer_entry_uuid). */
export function pricedEntryId(ctx: PlReadContext, rawEntry: unknown): string {
    let entry = rawEntry;
    if (isObj(entry)) entry = entryRestoreItemArr(entry);
    const id = isObj(entry) || Array.isArray(entry) ? (abilityEntryId(entry, ctx.entryDeps) || plCostLayerEntryUuid(entry)) : (typeof entry === 'string' ? entry : '');
    return checkUuid(id) ? id.toUpperCase() : '';
}
/** Cross-reference targets a definition's layers name. */
export function xrefTargetIds(ctx: PlReadContext, def: Row, contract: PlCostContract = plCostContract): string[] {
    const out = new Set<string>();
    const cats = plCatalogs(contract);
    const entries = (name: string): unknown[] => entriesList(ctx.layerEntries(def as ItemFieldRow, name));
    for (const layer of PL_STATUS_LAYERS) for (const e of entries(layer)) { const id = plCostLayerEntryUuid(e); if (id !== '' && cats.statusEffects.getVariableName(id) === null) out.add(id.toUpperCase()); }
    for (const layer of [...PL_CHARGE_GRANT_LAYERS, 'summon', ...PL_SHAPESHIFT_LAYERS]) for (const e of entries(layer)) { const id = plCostLayerEntryUuid(e); if (id !== '') out.add(id.toUpperCase()); }
    const kt = ctx.layerValue(def as ItemFieldRow, 'transform_killed_target_into_controlled_creature');
    if (kt !== null && kt !== undefined) { const id = plCostLayerEntryUuid(kt); if (id !== '' && checkUuid(id)) out.add(id.toUpperCase()); }
    const unlockLayers: string[] = ['spell_known'];
    for (const b of PL_UNLOCK_BASES) for (let i = 1; i <= 4; i++) unlockLayers.push('user_choice_' + i + '_' + b);
    for (const layer of unlockLayers) for (const e of entries(layer)) { const id = plCostLayerEntryUuid(e); if (id !== '') out.add(id.toUpperCase()); }
    return [...out];
}
/** Inventory TEMPLATE ids the inventory pass reads cost_gp from (a thinned instance's template; a bare-uuid inventory entry). */
export function inventoryTemplateIds(ctx: PlReadContext, root: Row): string[] {
    const out = new Set<string>();
    const sources: unknown[] = [...heldInventoryEntries(root),...entriesList(ctx.layerEntries(root as ItemFieldRow, 'inventory_equipped')), ...entriesList(ctx.layerEntries(root as ItemFieldRow, 'inventory_unequipped'))];
    for (const e of sources) {
        if (typeof e === 'string') { if (checkUuid(e)) out.add(e.toUpperCase()); continue; }
        if (!isObj(e) && !Array.isArray(e)) continue;
        const tpl = readInventoryTemplateId(e as ItemFieldRow); // _ft_inventory_template_id twin
        if (tpl) { out.add(tpl.toUpperCase()); continue; }
        const zero = (e as Row)['0'];
        if (checkUuid(zero)) out.add(zero.toUpperCase());
    }
    return [...out];
}
/** The ids the pricer will read for a root: resolve them all first (the entry fetches what the page lacks). */
export function plClosureIds(ctx: PlReadContext, root: Row, contract: PlCostContract = plCostContract): PlClosure {
    const steps = plClosureSteps(ctx, root, contract);
    let step = steps.next();
    while (!step.done) step = steps.next();
    return step.value;
}

export async function plClosureBackground(ctx: PlReadContext, root: Row, checkpoint: () => Promise<void>): Promise<PlClosure> {
    const steps = plClosureSteps(ctx, root, plCostContract);
    try {
        let step = steps.next();
        while (!step.done) { await checkpoint(); step = steps.next(); }
        return step.value;
    } finally { steps.return({ priced: [], targets: [], xref: [], total: 0 }); }
}

function* plClosureSteps(ctx: PlReadContext, root: Row, contract: PlCostContract): Generator<void, PlClosure> {
    const priced = new Set<string>();
    const entries = (name: string): unknown[] => entriesList(ctx.layerEntries(root as ItemFieldRow, name));
    for (const type of contract.abilityTypes) for (const e of entries(type)) { const id = pricedEntryId(ctx, e); if (id) priced.add(id); }
    for (const layer of PL_SPELL_LAYERS) for (const e of entries(layer)) { const id = pricedEntryId(ctx, e); if (id) priced.add(id); }
    for (const e of entries('spell_prepared_1')) { const id = typeof e === 'string' ? e : (Array.isArray(e) ? e[0] : isObj(e) ? e['0'] : ''); if (typeof id === 'string' && checkUuid(id)) priced.add(id.toUpperCase()); }
    const targets = new Set<string>(xrefTargetIds(ctx, root, contract));
    for (const t of inventoryTemplateIds(ctx, root)) targets.add(t);
    for (const pid of priced) {
        yield;
        const def = ctx.definition(pid);
        if (!def) continue; // reported by ctx.refused; the pricer refuses when it reaches it
        for (const t of xrefTargetIds(ctx, def, contract)) targets.add(t);
    }
    const xref = [...targets];
    for (const p of priced) targets.delete(p);
    return { priced: [...priced], targets: [...targets], xref, total: priced.size + targets.size };
}
